import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus, Pin, Lock, Send } from "lucide-react";
import { ForumRichEditor } from "@/components/community/ForumRichEditor";
import { useLocale } from "@/hooks/useLocale";
import { fmtDate, fmtDateTime } from "@/lib/locale-format";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

interface BoardThread {
  id: string;
  adventure_id: string;
  author_id: string;
  title: string;
  category: string;
  is_pinned: boolean;
  is_locked: boolean;
  post_count: number;
  last_post_at: string;
  created_at: string;
}

interface BoardPost {
  id: string;
  thread_id: string;
  author_id: string;
  content: string;
  is_edited: boolean;
  created_at: string;
}

interface Props {
  adventureId: string;
  currentUserId: string | null;
  senderName: string;
  isHost: boolean;
}

const CATEGORIES = ["general", "strategy", "trading", "lore"];

export function AdventureBoard({ adventureId, currentUserId, senderName, isHost }: Props) {
  const { t, locale } = useLocale();
  const [threads, setThreads] = useState<BoardThread[]>([]);
  const [selectedThread, setSelectedThread] = useState<BoardThread | null>(null);
  const [posts, setPosts] = useState<BoardPost[]>([]);
  const [showNewThread, setShowNewThread] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newCategory, setNewCategory] = useState("general");
  const [newContent, setNewContent] = useState("");
  const [replyContent, setReplyContent] = useState("");

  // Load threads
  useEffect(() => {
    loadThreads();
  }, [adventureId]);

  const loadThreads = async () => {
    const { data } = await supabase
      .from("adventure_board_threads")
      .select("*")
      .eq("adventure_id", adventureId)
      .order("is_pinned", { ascending: false })
      .order("last_post_at", { ascending: false })
      .limit(100) as any;
    if (data) setThreads(data);
  };

  const loadPosts = async (threadId: string) => {
    const { data } = await supabase
      .from("adventure_board_posts")
      .select("*")
      .eq("thread_id", threadId)
      .order("created_at", { ascending: true })
      .limit(200) as any;
    if (data) setPosts(data);
  };

  const handleCreateThread = async () => {
    if (!newTitle.trim() || !newContent.trim() || !currentUserId) return;
    const { data: thread, error } = await supabase
      .from("adventure_board_threads")
      .insert({
        adventure_id: adventureId,
        author_id: currentUserId,
        title: newTitle.trim(),
        category: newCategory,
      } as any)
      .select()
      .single() as any;
    if (error) { toast.error(t.play_send_failed); return; }
    // Create first post
    await supabase.from("adventure_board_posts").insert({
      thread_id: thread.id,
      author_id: currentUserId,
      content: newContent.trim(),
    } as any);
    setNewTitle("");
    setNewContent("");
    setShowNewThread(false);
    loadThreads();
  };

  const handleReply = async () => {
    if (!replyContent.trim() || !currentUserId || !selectedThread) return;
    const { error } = await supabase.from("adventure_board_posts").insert({
      thread_id: selectedThread.id,
      author_id: currentUserId,
      content: replyContent.trim(),
    } as any);
    if (error) { toast.error(t.play_send_failed); return; }
    setReplyContent("");
    loadPosts(selectedThread.id);
    loadThreads();
  };

  const handleTogglePin = async (thread: BoardThread) => {
    await supabase.from("adventure_board_threads").update({ is_pinned: !thread.is_pinned } as any).eq("id", thread.id);
    loadThreads();
  };

  const handleToggleLock = async (thread: BoardThread) => {
    await supabase.from("adventure_board_threads").update({ is_locked: !thread.is_locked } as any).eq("id", thread.id);
    loadThreads();
    if (selectedThread?.id === thread.id) setSelectedThread({ ...thread, is_locked: !thread.is_locked });
  };

  const openThread = (thread: BoardThread) => {
    setSelectedThread(thread);
    loadPosts(thread.id);
  };

  // ─── Thread View ───
  if (selectedThread) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border shrink-0">
          <Button variant="ghost" size="icon" className="h-6 w-6" aria-label="Back to thread list" onClick={() => setSelectedThread(null)}>
            <ArrowLeft className="w-3 h-3" />
          </Button>
          <span className="text-xs font-semibold truncate flex-1">{selectedThread.title}</span>
          {isHost && (
            <>
              <Button variant="ghost" size="icon" className="h-6 w-6" aria-label={selectedThread.is_pinned ? "Unpin thread" : "Pin thread"} onClick={() => handleTogglePin(selectedThread)}>
                <Pin className={`w-3 h-3 ${selectedThread.is_pinned ? "text-primary" : ""}`} />
              </Button>
              <Button variant="ghost" size="icon" className="h-6 w-6" aria-label={selectedThread.is_locked ? "Unlock thread" : "Lock thread"} onClick={() => handleToggleLock(selectedThread)}>
                <Lock className={`w-3 h-3 ${selectedThread.is_locked ? "text-destructive" : ""}`} />
              </Button>
            </>
          )}
        </div>
        <ScrollArea className="flex-1 px-3 py-2">
          <div className="space-y-2">
            {posts.map((post, i) => (
              <div key={post.id} className="rounded-md border border-border p-2 text-xs">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <span className="font-semibold text-foreground">#{i + 1}</span>
                  <span>{fmtDateTime(post.created_at, locale, { dateStyle: "short", timeStyle: "short" })}</span>
                  {post.is_edited && <span className="italic">(edited)</span>}
                </div>
                <div className="prose prose-xs dark:prose-invert max-w-none text-xs">
                  <ReactMarkdown>{post.content}</ReactMarkdown>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
        {!selectedThread.is_locked && (
          <div className="flex gap-1.5 p-2 border-t border-border shrink-0">
            <Input
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleReply(); } }}
              placeholder={t.mp_reply_placeholder}
              className="flex-1 h-7 text-xs bg-background"
            />
            <Button onClick={handleReply} disabled={!replyContent.trim()} size="icon" aria-label="Send reply" className="h-7 w-7">
              <Send className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>
    );
  }

  // ─── New Thread Form ───
  if (showNewThread) {
    return (
      <div className="flex flex-col h-full p-3 gap-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-6 w-6" aria-label="Cancel new thread" onClick={() => setShowNewThread(false)}>
            <ArrowLeft className="w-3 h-3" />
          </Button>
          <span className="text-xs font-semibold">{t.mp_new_thread}</span>
        </div>
        <Input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder={t.mp_thread_title}
          className="h-7 text-xs"
        />
        <div className="flex gap-1">
          {CATEGORIES.map((cat) => (
            <Badge
              key={cat}
              variant={newCategory === cat ? "default" : "outline"}
              className="cursor-pointer text-[10px] capitalize"
              onClick={() => setNewCategory(cat)}
            >
              {cat}
            </Badge>
          ))}
        </div>
        <ForumRichEditor value={newContent} onChange={setNewContent} placeholder={t.mp_write_post} minRows={3} />
        <Button onClick={handleCreateThread} disabled={!newTitle.trim() || !newContent.trim()} size="sm" className="self-end h-7 text-xs">
          {t.mp_create_thread}
        </Button>
      </div>
    );
  }

  // ─── Thread List ───
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border shrink-0">
        <span className="text-xs font-semibold">{t.mp_board_title}</span>
        <Button variant="ghost" size="icon" className="h-6 w-6" aria-label="New thread" onClick={() => setShowNewThread(true)}>
          <Plus className="w-3 h-3" />
        </Button>
      </div>
      <ScrollArea className="flex-1 px-2 py-1">
        {threads.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-6">{t.mp_no_threads}</p>
        ) : (
          <div className="space-y-1">
            {threads.map((thread) => (
              <button
                key={thread.id}
                onClick={() => openThread(thread)}
                className="w-full text-left rounded-md border border-border px-2.5 py-1.5 hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-center gap-1.5">
                  {thread.is_pinned && <Pin className="w-2.5 h-2.5 text-primary shrink-0" />}
                  {thread.is_locked && <Lock className="w-2.5 h-2.5 text-destructive shrink-0" />}
                  <span className="text-xs font-medium truncate flex-1">{thread.title}</span>
                  <Badge variant="outline" className="text-[9px] capitalize shrink-0">{thread.category}</Badge>
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
                  <span>{thread.post_count} {thread.post_count === 1 ? "post" : "posts"}</span>
                  <span>·</span>
                  <span>{fmtDate(thread.last_post_at, locale)}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
